import React, { useContext, useEffect, useState } from "react";
import R from "ramda";
import { Link } from "react-router-dom";
import { defineMessages, useIntl } from "react-intl";

import { useErrorController } from "@natera/platform/lib/hooks";
import { TransactionStatus } from "@natera/platform/lib/service/session/okta";
import { routes } from "@app/routing";
import { isValidLanguageValue, useQuery } from "@app/utils";
import {
  EnrollFactorResponse,
  FACTORS_TO_SHOW,
  IDP_TYPE,
} from "@app/service/user";
import {
  ErrorProvider,
  IntlContext,
  Language,
  NotificationContext,
  ServiceContext,
  SignUpContext,
  UserContext,
} from "@app/provider";
import { LoginStatus } from "@app/provider/user";
import { LoginForm } from "@app/components/forms";
import { usePortalRedirects, useTokenSourceFromQuery } from "@app/hooks";
import { IntlShape } from "react-intl/src/types";
import { AddNotification } from "@app/provider/notification";

import "../../../pages/public/signIn/signIn.scss";
import { Credentials } from "@app/service/session";

export enum OktaErrorCodes {
  AUTH_ERROR = "E0000004",
}

export const ATTEMPTS_COUNT_TO_SHOW = 2;

const messages = defineMessages({
  signInEmail: {
    id: "signInEmail",
    defaultMessage: "Email Address",
  },
  signInPasswordLabel: {
    id: "signInPasswordLabel",
    defaultMessage: "Password",
  },
  signInEmptyFactor: {
    id: "signInEmptyFactor",
    defaultMessage: "We're sorry. Something went wrong.",
  },
  signInForgotPassword: {
    id: "signInForgotPassword",
    defaultMessage: "Forgot password?",
  },
  signInForgotPasswordButton: {
    id: "signInForgotPasswordButton",
    defaultMessage: "Forgot Password?",
  },
  signInNeedAccount: {
    id: "signInNeedAccount",
    defaultMessage: "Already have an account?",
  },
  signInIncorrectCredentials: {
    id: "signInIncorrectCredentials",
    defaultMessage: "Incorrect email or password",
  },
  signInInactiveAccountLoginError: {
    id: "signInInactiveAccountLoginError",
    defaultMessage: "We're sorry. Something went wrong.",
  },
  signInTryAgainAccountActivated: {
    id: "signInTryAgainAccountActivated",
    defaultMessage:
      "Please try again using a different email and password combination.",
  },
  signInAttemptsRemain: {
    id: "signInAttemptsRemain",
    defaultMessage: "Attempts Remaining — {attempts}",
  },
  signInMaxAttemptsText: {
    id: "signInMaxAttemptsText",
    defaultMessage:
      "After 5 consecutive unsuccessful login attempts, your account will be locked",
  },
  signInAccountLocked: {
    id: "signInAccountLocked",
    defaultMessage: "Account Locked",
  },
  signInAccountLockedText: {
    id: "signInAccountLockedText",
    defaultMessage:
      "Your account has been locked due to multiple incorrect attempts.",
  },
  signInUnlockAccount: {
    id: "signInUnlockAccount",
    defaultMessage: "Unlock Account",
  },
  userStatusInactive: {
    id: "userStatusInactive",
    defaultMessage: "Your account has not been activated.",
  },
  highlightedTooltipDescriptionForm: {
    id: "highlightedTooltipDescriptionForm",
    defaultMessage: "You previously logged in with your Natera account.",
  },
});

const getFirstFilteredFactor = (
  factors?: EnrollFactorResponse[]
): EnrollFactorResponse | undefined => {
  if (!factors) return;
  const filteredFactorList = R.filter(
    (factor) => factor.factorType in FACTORS_TO_SHOW,
    factors
  );
  return filteredFactorList[0];
};

export const handleFactors = (
  factors: EnrollFactorResponse[],
  intl: IntlShape,
  addNotification: AddNotification,
  setFactor: React.Dispatch<
    React.SetStateAction<EnrollFactorResponse | undefined>
  >
): void => {
  const selectedFactor = getFirstFilteredFactor(factors);
  if (!selectedFactor) {
    addNotification({
      message: intl.formatMessage(messages.signInEmptyFactor),
      type: "error",
    });
  } else {
    setFactor(selectedFactor);
  }
};

const ConnectedLoginForm: React.FC = () => {
  const intl = useIntl();
  const query = useQuery();
  const { toLoginRedirect } = usePortalRedirects();

  const { loadProfile, logUserLogin, handleUserLogin } =
    useContext(UserContext);
  const { checkUserExisted } = useContext(SignUpContext);
  const { changeLanguage, currentLanguage } = useContext(IntlContext);
  const { addNotification, clear } = useContext(NotificationContext);
  const { sessionService } = useContext(ServiceContext);

  const errorController = useErrorController();
  const { token, lang: langFromQuery } = useTokenSourceFromQuery();

  const emailVerified = query.get("email_verified");
  const lang = query.get("lang") ?? langFromQuery;

  const [isLoginPending, setIsLoginPending] = useState(false);
  const [factor, setFactor] = useState<EnrollFactorResponse>();

  useEffect(() => {
    if (lang && currentLanguage !== lang && isValidLanguageValue(lang)) {
      changeLanguage(lang as Language);
    }
  }, []);

  interface ErrorResponse {
    errorSummary: string;
    errorCode: string;
    status?: string;
  }

  const getResetPasswordLink = () => (
    <Link to={routes.resetPassword} onClick={() => clear()}>
      {intl.formatMessage(messages.signInForgotPassword)}
    </Link>
  );

  const addNotificationInactiveAccountLoginFailure = () => {
    addNotification({
      type: "error",
      message: intl.formatMessage(messages.signInInactiveAccountLoginError),
    });
  };

  const addNotificationAttemptsRemaining = (
    attemptsLeft?: number,
    isAccountActivated?: boolean
  ) => {
    if (!isAccountActivated) {
      addNotificationInactiveAccountLoginFailure();
      return;
    }

    addNotification({
      title: intl.formatMessage(messages.signInIncorrectCredentials),
      message: (
        <>
          <div>
            {intl.formatMessage(messages.signInAttemptsRemain, {
              attempts: attemptsLeft,
            })}
          </div>
          <div>{intl.formatMessage(messages.signInMaxAttemptsText)}</div>
        </>
      ),
      actions: getResetPasswordLink(),
      type: "warning",
    });
  };

  const addNotificationTryAgain = (isAccountActivated?: boolean) => {
    if (!isAccountActivated) {
      addNotificationInactiveAccountLoginFailure();
      return;
    }

    addNotification({
      title: intl.formatMessage(messages.signInIncorrectCredentials),
      message: intl.formatMessage(messages.signInTryAgainAccountActivated),
      actions: getResetPasswordLink(),
      type: "warning",
    });
  };

  const handleLogUserLogin = async (email: string) => {
    try {
      const data = await logUserLogin({
        email,
        status: LoginStatus.FAIL,
        token,
      });

      if (!data) {
        throw new Error();
      }

      const { attemptsLeft, isAccountActivated } = data;

      if (attemptsLeft && attemptsLeft <= ATTEMPTS_COUNT_TO_SHOW) {
        addNotificationAttemptsRemaining(attemptsLeft, isAccountActivated);
      } else {
        addNotificationTryAgain(isAccountActivated);
      }
    } catch (e) {
      addNotification({
        title: intl.formatMessage(messages.signInIncorrectCredentials),
        type: "warning",
      });
    }
  };

  const handleError = async (err: ErrorResponse, email: string) => {
    switch (err.errorCode) {
      case OktaErrorCodes.AUTH_ERROR:
        await handleLogUserLogin(email);
        break;
      default:
        if (err.status === TransactionStatus.LOCKED_OUT) {
          const actions = (
            <Link to={routes.unlock} state={email}>
              {intl.formatMessage(messages.signInUnlockAccount)}
            </Link>
          );
          addNotification({
            title: intl.formatMessage(messages.signInAccountLocked),
            message: intl.formatMessage(messages.signInAccountLockedText),
            actions,
            type: "error",
          });
        } else {
          addNotification({
            type: "error",
          });
        }
        break;
    }
  };

  const handleSubmit = async (props: Credentials) => {
    setIsLoginPending(true);
    clear();
    try {
      if (await sessionService.getToken()) {
        await sessionService.clearToken();
      }

      const isUserExist = await checkUserExisted(props.email);
      if (
        isUserExist?.isExisted &&
        isUserExist.idpType &&
        isUserExist.idpType !== IDP_TYPE.EMAIL
      ) {
        toLoginRedirect({
          params: {
            provider: isUserExist.idpType.toUpperCase() as IDP_TYPE,
            email: props.email,
            ...(token ? { token } : {}),
          },
          preserveQuery: false,
        });
        return;
      }

      const transaction = await sessionService.login(props);
      const status = transaction.transaction.status;
      if (status === TransactionStatus.LOCKED_OUT) {
        throw { status };
      }
      await logUserLogin({
        email: props.email,
        status: LoginStatus.SUCCESS,
        token,
      });
      const factors = transaction.transaction.factors as EnrollFactorResponse[];
      if (factors && factors?.length > 0) {
        handleFactors(factors, intl, addNotification, setFactor);
      } else {
        await handleUserLogin();
        await loadProfile();
      }
    } catch (error) {
      await handleError(error, props.email);
    } finally {
      setIsLoginPending(false);
    }
  };

  return (
    <section className="form__container">
      <ErrorProvider controller={errorController}>
        <LoginForm
          setValidationError={errorController.setValidationError}
          clearValidationError={errorController.clearValidationError}
          clearErrors={errorController.clearErrors}
          isLoading={isLoginPending}
          onSubmit={handleSubmit}
          prefilledEmail={emailVerified || undefined}
        />
      </ErrorProvider>
    </section>
  );
};

export default ConnectedLoginForm;
